import { d3, wordCloud as wordCloudLayout } from './d3';

const LAYOUT = {
  width: 960,
  height: 540,
};
const MARGIN = {
  top: LAYOUT.height * 0.05,
  right: LAYOUT.width * 0.05,
  bottom: LAYOUT.height * 0.05,
  left: LAYOUT.width * 0.05,
};
const WORD_PADDING = 0.9;
const ANIMATION_DURATION = 700;
const FONT_BASE_SIZE = 5;
const FONT_SIZE_SCALE = 3;
const NB_MAIN_WORDS = 3;
const MAX_LENGTH_VERTICAL_WORD = 12; // Words of 12+ chars will always be horizontal
const MIN_LENGTH_HORIZONTAL_WORD = 2; // Words of 2 chars or less will be horizontal
const VERTICAL_WORDS_PROPORTION = 0.5; // 50% of (small) words will be vertical
const RECT_OPACITY = 0.7;

export interface WordCloudResultT {
  text: string;
  count: number;
}

export interface WordCloudData {
  key: string;
  text: string;
  value?: number;
  columnId?: number;
  index?: number;
  show?: boolean;
  i?: number;
  emojiUrl?: string;
  size?: number;
  x?: number;
  y?: number;
  rotate?: number;
  bbox?: DOMRect;
}
interface Scale {
  width: number;
  height: number;
  bounds: { width: number; height: number };
}
interface T {
  selector: string;
  data: WordCloudData[];
  emojiCloud: boolean;
  font: string;
  animationMultiplier: 1 | 0;
  moderationEnabled: boolean;
  fillColors: string[];
  removeWord: (key: string) => void;
}

/**
 * WordCloud: animating visualization that sizes and arranges words based on popularity
 */
export class WordCloud implements T {
  readonly selector: string;
  readonly data: WordCloudData[];
  readonly emojiCloud: boolean;
  readonly font: string;
  readonly animationMultiplier: 1 | 0;
  readonly moderationEnabled: boolean;
  readonly fillColors: string[];
  readonly removeWord;

  /**
   * @param selector: string, selection of a dom node, e.g., #wordcloud-${questionId}
   * @param data: result data with words and counts
   * @param emojiCloud: boolean, specifies if the word cloud contains emojis only
   * @param font: string, specifies a font for the word cloud
   * @param animationMultiplier: value to multiply animation durations by, 1 (enabled) or 0 (disabled)
   * @param fillColors: array of color strings, e.g., ['blue', 'green', 'red']
   * @param moderationEnabled: boolean, specifies whether words can be removed from the word cloud
   * @param removeWord: function that removes a word from the result data
   */
  constructor(props: T) {
    this.selector = props.selector;
    this.data = props.data;
    this.emojiCloud = props.emojiCloud;
    this.font = props.font;
    this.animationMultiplier = props.animationMultiplier;
    this.fillColors = props.fillColors;
    this.moderationEnabled = props.moderationEnabled;
    this.removeWord = props.removeWord;
  }

  /**
   * Determines whether a word should be rotated 90deg for optimal positioning in the word cloud
   *
   * @param key: string, word text
   * @param i: number, index of the current word
   * @returns number, rotation in degrees
   */
  private getRotation = ({ key }: { key: string }, i: number): number => {
    let rotation = 0;
    const wordShouldRotate = !(
      i < NB_MAIN_WORDS ||
      key.length >= MAX_LENGTH_VERTICAL_WORD ||
      key.length <= MIN_LENGTH_HORIZONTAL_WORD
    );
    if (wordShouldRotate) {
      rotation = Math.random() < VERTICAL_WORDS_PROPORTION ? -90 : 0;
    }
    return rotation;
  };

  /**
   * Logarithmic growth pattern for font size. Aims to maximize
   * difference in size for small vote counts and level differences
   * for larger counts.
   *
   * @param value: number of occurences of a word
   * @returns number, font size in pixels
   */
  private getFontSize = ({ value }: { value: number }): number => {
    // log(1) is 0, log(2) is 0.69, log(100000) is 11.5
    const fontSize = FONT_BASE_SIZE * Math.log(value * FONT_SIZE_SCALE);
    return fontSize;
  };

  /**
   * Calculates the scale to which a word should be transformed
   *
   * @param width: number, width of the visualization in pixels
   * @param height: number, height of the visualization in pixels
   * @param bounds: computed bounding width and height of a text element in pixels
   * @returns number, transform scale value
   */
  private getScale = ({ width, height, bounds }: Scale): number => {
    const scaleWidth = width / bounds.width;
    const scaleHeight = height / bounds.height;
    return Math.min(scaleWidth, scaleHeight);
  };

  public update = async (showResults: boolean): Promise<void[]> => {
    const { moderationEnabled, animationMultiplier, getScale } = this;
    const width = LAYOUT.width - MARGIN.left - MARGIN.right;
    const height = LAYOUT.height - MARGIN.top - MARGIN.bottom;

    function mouseIsOut(this: d3.BaseType) {
      if (showResults) {
        d3.select(this).attr('text-decoration', 'none').attr('opacity', 1);
      }
    }
    function mouseIsOver(this: d3.BaseType) {
      if (moderationEnabled && showResults) {
        d3.select(this)
          .attr('text-decoration', 'line-through')
          .attr('opacity', 0.3);
      }
    }

    const wordCloud = wordCloudLayout();
    wordCloud()
      .overflow(true)
      .size([width, height])
      // WARNING!! MAKE SURE THIS IS THE FONT USE WHEN DISPLAYING THE WORDCLOUD
      // otherwise, you're just checking for collision on the wrong glyphs! :-)
      .font(this.font)
      .text((d: WordCloudData) => d.text)
      .fontSize(this.getFontSize)
      .padding(WORD_PADDING)
      .spiral('archimedean')
      .rotate(this.getRotation)
      .words(this.data)
      .start();

    const container = d3
      .select(this.selector)
      .attr(
        'transform',
        `translate(${LAYOUT.width / 2}, ${LAYOUT.height / 2})`,
      );
    const svg = container
      .selectAll('.wordcloud')
      .data(['']) // Only append one
      .join('g')
      .attr('class', 'wordcloud');

    // Select
    const text = svg
      .selectAll<d3.BaseType, WordCloudData>('.text-group')
      .data(this.data, (d) => d.key)
      .join((enter) => {
        const g = enter
          .append('g')
          .attr('class', 'text-group')
          .attr('opacity', 0);

        // text
        g.append('text')
          .attr('text-anchor', 'middle')
          .attr('alignment-baseline', 'center')
          .attr(
            'fill',
            (_, i) => this.fillColors[i % this.fillColors.length] ?? '',
          )
          .text((d) => d.text)
          .each(function (this: d3.BaseType, d) {
            d.bbox = (this as SVGTextElement)?.getBBox?.();
          });

        // emoji
        g.append('image')
          .attr('xlink:href', (d) => d.emojiUrl ?? '')
          .each(function (this, d) {
            if (!d.emojiUrl) return;
            d.bbox = (this as SVGImageElement)?.getBBox?.();
          });

        // box
        g.insert('rect', ':first-child')
          .attr('opacity', 0)
          .attr(
            'fill',
            (_, i) => this.fillColors[i % this.fillColors.length] ?? '',
          )
          .attr('x', 0)
          .attr('y', 0)
          .attr('width', 0)
          .attr('height', 0);

        return g;
      });

    // Update
    const transition = text
      .conditionalTransition({
        name: 'text-group',
        duration: ANIMATION_DURATION * animationMultiplier,
      })
      .attr('opacity', 1)
      .end();

    const textTransition = text
      .select('text')
      .text((d) => d.text)
      .attr('display', (d) => (d.emojiUrl ? 'none' : 'block'))
      .attr('cursor', moderationEnabled ? 'pointer' : 'default')
      .on('mouseover', mouseIsOver)
      .on('mouseout', mouseIsOut)
      .on('click', (_event, d) => this.removeWord(d.key))
      .attr('font-size', (d) => `${d.size}px`)
      .conditionalTransition({
        duration: ANIMATION_DURATION * animationMultiplier,
      })
      .attr('transform', (d) => `translate(${d.x}, ${d.y}) rotate(${d.rotate})`)
      .attr('fill', (_, i) => this.fillColors[i % this.fillColors.length] ?? '')
      .attr('opacity', showResults ? 1 : 0)
      .end();

    const emojiTransition = text
      .select('image')
      .filter((d) => Boolean(d.emojiUrl))
      .attr('xlink:href', (d) => d.emojiUrl ?? '')
      .attr('cursor', moderationEnabled ? 'pointer' : 'default')
      .on('mouseover', mouseIsOver)
      .on('mouseout', mouseIsOut)
      .on('click', (_event, d) => this.removeWord(d.key))
      .conditionalTransition({
        duration: ANIMATION_DURATION * animationMultiplier,
      })
      .attr('width', (d) => d.size ?? 0)
      .attr('height', (d) => d.size ?? 0)
      .attr('x', (d) => (d.size ? -d.size / 2 : 0))
      .attr('y', (d) =>
        d.size ? (this.emojiCloud ? -d.size / 2 : -d.size) : 0,
      )
      .attr('opacity', showResults ? 1 : 0)
      .attr('transform', (d) => `translate(${d.x}, ${d.y}) rotate(${d.rotate})`)
      .end();

    text.select('text').each(function (this: d3.BaseType, d) {
      d.bbox = (this as SVGTextElement)?.getBBox?.();
    });

    text.select('image').each(function (this, d) {
      if (!d.emojiUrl) return;
      d.bbox = (this as SVGTextElement)?.getBBox?.();
    });

    const RECT_PADDING_X = 5;
    const RECT_HEIGHT_MULTIPLIER = 0.8;
    const blockTransition = text
      .select('rect')
      .conditionalTransition({
        name: 'rect',
        duration: ANIMATION_DURATION * animationMultiplier,
      })
      .attr('rx', (d: WordCloudData) =>
        d.bbox?.height ? d.bbox?.height * 0.5 : 0,
      )
      .attr('x', (d: WordCloudData) =>
        d.bbox?.x ? d.bbox?.x - RECT_PADDING_X / 2 : 0,
      )
      .attr('y', (d: WordCloudData) =>
        d.bbox?.y
          ? d.bbox.y + (d.bbox.height * (1 - RECT_HEIGHT_MULTIPLIER)) / 2
          : 0,
      )
      .attr('width', (d: WordCloudData) =>
        d.bbox?.width ? d.bbox?.width + RECT_PADDING_X : 0,
      )
      .attr('height', (d: WordCloudData) =>
        d.bbox?.height ? d.bbox.height * RECT_HEIGHT_MULTIPLIER : 0,
      )
      .attr('transform', (d) => `translate(${d.x}, ${d.y}) rotate(${d.rotate})`)
      .attr('fill', (_, i) => this.fillColors[i % this.fillColors.length] ?? '')
      .attr('opacity', showResults ? 0 : RECT_OPACITY)
      .end();

    // Exit
    text
      .exit()
      .conditionalTransition({
        duration: ANIMATION_DURATION * animationMultiplier,
      })
      .attr('opacity', 0)
      .remove();

    // scale entire cloud to fit viewport, using its current bounding box
    function scaleWordCloud(): Promise<void[]> {
      const bounds =
        svg && svg.node() && (svg.node() as SVGTextElement).getBBox
          ? (svg.node() as SVGTextElement).getBBox()
          : { width: 0, height: 0 };
      const promises = [];
      if (bounds.width > 0 && bounds.height > 0) {
        const scale = getScale({ width, height, bounds });
        const scaleTransition = svg
          .conditionalTransition({
            name: 'scale-wc',
            duration: ANIMATION_DURATION * animationMultiplier,
          })
          .attr('transform', `scale(${scale})`)
          .end();
        promises.push(scaleTransition);
      }
      return Promise.all(promises);
    }

    return Promise.all([
      transition,
      textTransition,
      emojiTransition,
      blockTransition,
    ]).then(scaleWordCloud);
  };
}
